import React, { useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const Lightbox = ({ 
  isOpen, 
  onClose, 
  photos, 
  currentIndex, 
  onPrevious, 
  onNext 
}) => {
  const { isMarathi } = useLanguage();

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (!isOpen) return;
    
    switch (e.key) {
      case 'Escape':
        onClose();
        break;
      case 'ArrowLeft':
        onPrevious();
        break;
      case 'ArrowRight':
        onNext();
        break;
      default:
        break;
    }
  }, [isOpen, onClose, onPrevious, onNext]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen || !photos || photos.length === 0) {
    return null;
  }

  const currentPhoto = photos[currentIndex];

  return (
    <div 
      className={`lightbox-overlay ${isOpen ? 'active' : ''}`}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={isMarathi() ? 'छायाचित्र दृश्य' : 'Photo viewer'}
    >
      <div 
        className="lightbox-container"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          className="lightbox-close"
          onClick={onClose}
          aria-label={isMarathi() ? 'बंद करा' : 'Close'}
        >
          ×
        </button>

        {/* Photo Counter */}
        <div className="lightbox-counter">
          {currentIndex + 1} / {photos.length}
        </div>

        {/* Image Container */}
        <div className="lightbox-image-container">
          <picture>
            {currentPhoto.image && <source srcSet={currentPhoto.image} type="image/webp" />}
            <img
              src={currentPhoto.fallback || currentPhoto.url}
              alt={isMarathi() ? (currentPhoto.title?.marathi || '') : (currentPhoto.title?.english || '')}
              className="lightbox-image"
              loading="eager"
            />
          </picture>
        </div>

        {/* Image Info */}
        <div className="lightbox-info">
          <h3 className="lightbox-title">
            {isMarathi() ? (currentPhoto.title?.marathi || '') : (currentPhoto.title?.english || '')}
          </h3>
          <p className="lightbox-description">
            {isMarathi() ? (currentPhoto.description?.marathi || '') : (currentPhoto.description?.english || '')}
          </p>
        </div>

        {/* Navigation Arrows */}
        {photos.length > 1 && (
          <>
            <button
              className="lightbox-nav lightbox-prev"
              onClick={onPrevious}
              aria-label={isMarathi() ? 'मागील छायाचित्र' : 'Previous photo'}
            >
              ‹
            </button>
            <button
              className="lightbox-nav lightbox-next"
              onClick={onNext}
              aria-label={isMarathi() ? 'पुढील छायाचित्र' : 'Next photo'}
            >
              ›
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default Lightbox;
